/* eslint-disable no-unused-vars */
import PropTypes from 'prop-types';
import React, { useState } from 'react';

// import { Link as RouterLink, useLocation } from 'react-router-dom';
// import numeral from 'numeral';

import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import FileCopyIcon from '@mui/icons-material/FileCopyRounded';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDownRounded';

import {
  Button,
  Card,
  Divider,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  TableContainer,
  Tooltip,
  Typography
} from '@mui/material';

import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import toast from 'react-hot-toast';
import checkActionAllowed from 'src/config/AccessControl/checkActionAllowed';
import Action from 'src/config/models/Action';

import { DataGrid } from '@mui/x-data-grid';
import useAuth from 'src/hooks/useAuth';

import exportPDF from '../ReportExportPDF/exportPDF';
import SuspenseLoader from '../SuspenseLoader';
import createExportData from './createExportData';
import initTableData from './getTableData';

const escapeCSV = (value) => {
  if (value === null || value === undefined) return '';
  const str = String(value);
  return `"${str.replace(/"/g, '""')}"`;
};

const exportCSV = async (generateFileName = () => 'data.csv', generateData) => {
  const fileName = generateFileName();
  try {
    toast.loading('Generating CSV...', { id: fileName });
    const [headings, tableData] = await generateData();
    if (!headings || !tableData) {
      toast.error('Failed to generate CSV', { id: fileName });
      return;
    }

    toast.success(`Downloaded ${fileName}`, { id: fileName });

    let csvContent = headings.join(',');

    tableData.forEach((row) => {
      csvContent += `\n${row.map(escapeCSV).join(',')}`;
    });

    let blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    let link = document.createElement('a');
    let url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `${fileName}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (e) {
    toast.error('Failed to generate CSV', { id: fileName });
    console.error(e);
  }
};

const BrandTable = ({
  tableConfig,
  tableData = [],
  primaryKey,
  loading,
  hideColumns = [],
  noResultsMessage,
  actions,
  onEditAction,
  onDeleteAction,
  itemCount,
  generateFileName,
  paginationModel,
  setPaginationModel,
  preExportHook,
  csvConfig = tableConfig,
  tableFilter,
  preCopyHook = async () => {},
  dataLength,
  dataLoaded,
  isPDFDownload = true
}) => {
  const { user, permissions } = useAuth();

  const isAdmin = user.role === 'SUPER_ADMIN';

  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const exportMenuOpen = Boolean(exportAnchorEl);

  const [actionsOpen, setActionsOpen] = useState(null);
  const [popover, setPopover] = useState(null);

  const [copyAnchorEl, setCopyAnchorEl] = useState(null);
  const copyMenuOpen = Boolean(copyAnchorEl);

  const editEnabled = onEditAction && onEditAction instanceof Action;
  // &&
  // (isAdmin || checkActionAllowed(onEditAction, permissions));
  const deleteEnabled = onDeleteAction && onDeleteAction instanceof Action;

  const openActionsMenu = (id) => (e) => {
    setActionsOpen(e.currentTarget);
    setPopover(id);
  };

  const allowedActions =
    actions?.filter(
      (action) => isAdmin || checkActionAllowed(action, permissions)
    ) ?? [];

  const { rows, columns } = initTableData(tableConfig, tableData, primaryKey);

  const preExport = async () => {
    const exportData = (await preExportHook?.()) || tableData;
    if (!exportData?.length) {
      toast.error('No export data');
      return [];
    }
    const { rows } = initTableData(csvConfig, exportData, primaryKey);
    return rows;
  };

  if (editEnabled || deleteEnabled || allowedActions?.length)
    columns.push({
      field: 'idx',
      headerName: 'Actions',
      sortable: false,
      filter: false,
      width: '100',
      disableColumnMenu: true,
      disableExport: true,
      renderCell: ({ value }) => {
        const idx = value - 1;
        const keyId = tableData[idx]?.id || tableData[idx]?.[primaryKey];

        return (
          <Tooltip title="Open Actions" key={`actions-tooltip-${keyId}`}>
            <IconButton
              color="primary"
              onClick={openActionsMenu(idx)}
              aria-describedby={`actions-popover-${keyId}`}
              id={`actions-button-${keyId}`}
              key={`actions-button-${keyId}`}
            >
              <MoreVertRoundedIcon />
            </IconButton>
          </Tooltip>
        );
      }
    });

  const handleExportClick = (event) => {
    setExportAnchorEl(event.currentTarget);
  };
  const handleCloseExport = () => {
    setExportAnchorEl(null);
  };

  const handleCopyClick = (event) => {
    setCopyAnchorEl(event.currentTarget);
  };

  const handleCloseCopy = () => {
    setCopyAnchorEl(null);
  };

  const handleCopyColumn = async (config) => {
    handleCloseCopy();
    try {
      const data = (await preCopyHook()) || tableData;
      const values = data.map((item) => {
        if (typeof config.accessor === 'function') {
          return config.accessor(item);
        }
        return item[config.accessor] || '';
      });

      const textToCopy = values
        .filter((v) => v !== null && v !== undefined)
        .join('\n');
      navigator.clipboard.writeText(textToCopy);
      toast.success(`Copied ${config.headerName} to clipboard`);
    } catch (error) {
      console.error('Failed to copy', error);
      toast.error('Failed to copy to clipboard');
    }
  };

  const closeActionsMenu = () => {
    setActionsOpen(null);
    setPopover(null);
  };

  const createActionMenuHandler = (id) => (clickHandler) => () => {
    clickHandler?.(id);
    closeActionsMenu();
  };

  const createOtherActionHandler = (data) => (clickHandler) => () => {
    clickHandler?.(data);
    closeActionsMenu();
  };

  const keyId = tableData?.[popover]?.id || tableData?.[popover]?.[primaryKey];

  return (
    <>
      {Boolean(editEnabled || deleteEnabled || allowedActions?.length) &&
        popover !== null && (
          <Popover
            key={`actions-popover-${keyId}`}
            id={`actions-popover-${keyId}`}
            open={popover !== null}
            disableScrollLock
            anchorEl={actionsOpen}
            onClose={closeActionsMenu}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
          >
            <List
              sx={{
                p: 1
              }}
              component="nav"
            >
              {editEnabled && (
                <ListItemButton
                  button
                  onClick={createActionMenuHandler(keyId)(onEditAction.run)}
                >
                  <ListItemIcon>
                    <EditRoundedIcon color="warning" fontSize="small" />
                  </ListItemIcon>

                  <ListItemText primary="Edit" />
                </ListItemButton>
              )}
              {deleteEnabled && (
                <ListItemButton
                  button
                  onClick={createActionMenuHandler(keyId)(onDeleteAction.run)}
                >
                  <ListItemIcon>
                    <DeleteRoundedIcon color="error" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Delete" />
                </ListItemButton>
              )}

              {Boolean(
                (editEnabled || deleteEnabled) && allowedActions?.length
              ) && <Divider />}

              {allowedActions?.map((action, actionIdx) => {
                const isDisabled = action.meta?.shouldDisable?.(
                  tableData[popover]
                );
                const tooltip =
                  typeof action.meta.tooltip === 'function'
                    ? action.meta.tooltip(tableData[popover])
                    : action.meta.tooltip;

                return (
                  <React.Fragment key={actionIdx}>
                    {!action.meta.shouldHide?.(tableData[popover]) && (
                      <Tooltip title={tooltip || ''}>
                        <span>
                          <ListItemButton
                            onClick={createOtherActionHandler(
                              tableData[popover]
                            )(action.run)}
                            disabled={isDisabled}
                          >
                            {action.meta.icon && (
                              <ListItemIcon>{action.meta.icon}</ListItemIcon>
                            )}
                            <ListItemText
                              primary={action.label ?? action?.label}
                            />
                          </ListItemButton>
                        </span>
                      </Tooltip>
                    )}
                  </React.Fragment>
                );
              })}
            </List>
          </Popover>
        )}
      <Card>
        <TableContainer>
          {loading ? (
            dataLength && dataLoaded ? (
              <SuspenseLoader total={dataLength} current={dataLoaded} />
            ) : (
              <SuspenseLoader />
            )
          ) : null}

          {Boolean(rows?.length === 0 && !loading) && (
            <Typography
              variant="h3"
              fontWeight="normal"
              color="text.secondary"
              textAlign="center"
              my={22}
            >
              {noResultsMessage ?? 'Apply filters to see the results'}
            </Typography>
          )}
          {Boolean(rows.length) && !loading && (
            <>
              <Grid container spacing={2} p={2}>
                <Grid
                  item
                  sm="12"
                  md="10"
                  lg="10"
                  display="flex"
                  justifyContent="flex-start"
                >
                  {tableFilter}
                </Grid>
                <Grid
                  item
                  maxHeight={65}
                  sm="12"
                  md="2"
                  lg="2"
                  display="flex"
                  justifyContent="flex-end"
                  gap={1}
                >
                  <Button
                    variant="outlined"
                    startIcon={<ContentCopyRoundedIcon />}
                    onClick={handleCopyClick}
                  >
                    Copy
                  </Button>
                  <Menu
                    anchorEl={copyAnchorEl}
                    open={copyMenuOpen}
                    onClose={handleCloseCopy}
                    PaperProps={{
                      style: {
                        maxHeight: 300,
                        width: '20ch'
                      }
                    }}
                  >
                    {tableConfig.map((config, index) => (
                      <MenuItem
                        key={index}
                        onClick={() => handleCopyColumn(config)}
                      >
                        <Typography variant="inherit" noWrap>
                          {config.Header}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Menu>
                  <Button
                    id="demo-customized-button"
                    aria-controls={
                      exportMenuOpen ? 'demo-customized-menu' : undefined
                    }
                    aria-haspopup="true"
                    aria-expanded={exportMenuOpen ? 'true' : undefined}
                    variant="outlined"
                    disableElevation
                    onClick={handleExportClick}
                    endIcon={<KeyboardArrowDownIcon />}
                  >
                    Export
                  </Button>
                  <Menu
                    id="demo-customized-menu"
                    MenuListProps={{
                      'aria-labelledby': 'demo-customized-button'
                    }}
                    anchorEl={exportAnchorEl}
                    open={exportMenuOpen}
                    onClose={handleCloseExport}
                    PaperProps={{
                      style: {
                        minWidth: '20ch'
                      }
                    }}
                  >
                    {isPDFDownload && (
                      <MenuItem
                        onClick={() => {
                          handleCloseExport();
                          exportPDF(generateFileName, () =>
                            createExportData(columns, rows, 'pdf', preExport)
                          );
                        }}
                      >
                        <ListItemIcon>
                          <PictureAsPdfRoundedIcon color="error" />
                        </ListItemIcon>
                        <Typography variant="inherit" noWrap>
                          PDF
                        </Typography>
                      </MenuItem>
                    )}
                    {isPDFDownload && <Divider sx={{ my: 0.5 }} />}
                    <MenuItem
                      onClick={() => {
                        handleCloseExport();
                        exportCSV(generateFileName, () =>
                          createExportData(columns, rows, 'sheet', preExport)
                        );
                      }}
                    >
                      <ListItemIcon>
                        <FileCopyIcon />
                      </ListItemIcon>
                      <Typography variant="inherit" noWrap>
                        CSV
                      </Typography>
                    </MenuItem>
                  </Menu>
                </Grid>
              </Grid>
              <Divider />
              <DataGrid
                columns={columns}
                rows={rows}
                onPaginationModelChange={setPaginationModel}
                // checkboxSelection
                disableRowSelectionOnClick
                paginationMode={setPaginationModel ? 'server' : 'client'}
                initialState={{
                  pagination: {
                    paginationModel: paginationModel || {
                      pageSize: 10,
                      page: 0
                    }
                  },
                  columns: {
                    columnVisibilityModel: hideColumns.reduce(
                      (acc, column) => ({ ...acc, [column]: false }),
                      {}
                    )
                  }
                }}
                // getRowId={({ id }) => id || v4()}
                pagination
                rowCount={itemCount || tableData.length}
                pageSizeOptions={[5, 10, 25, 50, 100]}
              />
            </>
          )}
        </TableContainer>
      </Card>
    </>
  );
};

BrandTable.propTypes = {
  tableData: PropTypes.array.isRequired,
  tableConfig: PropTypes.array.isRequired,
  loading: PropTypes.bool
};

BrandTable.defaultProps = {
  products: [],
  tableConfig: [],
  loading: false
};

export default BrandTable;
